import { html, nothing } from "lit";
import type { FleetProjectSummary } from "../types.ts";

export type FleetProjectsPanelProps = {
  loading: boolean;
  error: string | null;
  projects: FleetProjectSummary[];
  activeProjectId: string | null;
  connected: boolean;
  onRefresh: () => void;
  onDiscover: () => void;
  onCreate: (name: string) => void;
  onOpen: (project: FleetProjectSummary) => void;
  onRename: (project: FleetProjectSummary, name: string) => void;
  onDelete: (project: FleetProjectSummary) => void;
};

function formatUpdated(ms: number): string {
  if (!ms) {
    return "";
  }
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "";
  }
}

export function renderFleetProjectsPanelBody(props: FleetProjectsPanelProps) {
  return html`
    <div class="fleet-projects-panel">
      <div class="fleet-projects-panel__toolbar">
        <button
          type="button"
          class="btn btn--sm btn--primary"
          ?disabled=${!props.connected || props.loading}
          @click=${() => {
            const name = window.prompt("New project name");
            if (name?.trim()) {
              props.onCreate(name.trim());
            }
          }}
        >
          New project
        </button>
        <button
          type="button"
          class="btn btn--sm"
          ?disabled=${!props.connected || props.loading}
          title="Register folders under .openclaw/fleet-projects that are not in the list yet"
          @click=${() => props.onDiscover()}
        >
          Scan disk
        </button>
        <button
          type="button"
          class="btn btn--sm"
          ?disabled=${!props.connected || props.loading}
          @click=${() => props.onRefresh()}
        >
          Refresh
        </button>
      </div>
      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}
      ${
        props.loading
          ? html`
              <p class="muted">Loading projects…</p>
            `
          : props.projects.length === 0
            ? html`
                <p class="muted fleet-projects-panel__empty">
                  No projects for this chat yet. Agents can still write files — see the
                  <strong>Project files</strong> tab (chat-only and subagent folders). Create a project here for a
                  dedicated session tab under <code>.openclaw/fleet-projects/&lt;slug&gt;/</code>. To run
                  <strong>Self improvement</strong>, open the <strong>Chat</strong> tab, enable the checkbox under
                  the composer, and send your goal.
                </p>
              `
            : html`
                <ul class="fleet-projects-panel__list" role="list">
                  ${props.projects.map((project) => {
                    const active = props.activeProjectId === project.id;
                    return html`
                      <li
                        class="fleet-projects-panel__item ${active ? "fleet-projects-panel__item--active" : ""}"
                      >
                        <div class="fleet-projects-panel__item-main">
                          <span class="fleet-projects-panel__name">${project.name}</span>
                          <span class="fleet-projects-panel__meta muted">
                            ${project.fileCount} file${project.fileCount === 1 ? "" : "s"}
                            ${
                              formatUpdated(project.updatedAtMs)
                                ? html` · ${formatUpdated(project.updatedAtMs)}`
                                : nothing
                            }
                          </span>
                        </div>
                        <div class="fleet-projects-panel__actions" role="toolbar">
                          <button
                            type="button"
                            class="btn btn--sm"
                            title="Open project chat and files"
                            @click=${() => props.onOpen(project)}
                          >
                            Open
                          </button>
                          <button
                            type="button"
                            class="btn btn--sm"
                            title="Rename project"
                            @click=${() => {
                              const next = window.prompt("Project name", project.name);
                              if (next?.trim() && next.trim() !== project.name) {
                                props.onRename(project, next.trim());
                              }
                            }}
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            class="btn btn--sm btn--danger"
                            title="Delete project and all files"
                            @click=${() => {
                              const ok = window.confirm(
                                `Delete project "${project.name}"?\n\nThis permanently removes all files in the project folder and deletes the linked chat session. This cannot be undone.`,
                              );
                              if (ok) {
                                props.onDelete(project);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </li>
                    `;
                  })}
                </ul>
              `
      }
    </div>
  `;
}

export function formatFleetProjectsPanelMeta(props: {
  loading: boolean;
  projects: FleetProjectSummary[];
}): string {
  if (props.loading) {
    return "Loading…";
  }
  const n = props.projects.length;
  return `${n} project${n === 1 ? "" : "s"}`;
}
